import React, { forwardRef } from 'react';
import { cn } from '../../../utils/cn';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helperText?: string;
  errorText?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, helperText, errorText, size = 'md', className, id, ...props }, ref) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!errorText;

    const sizeClasses = {
      sm: 'h-3 w-3',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
    };

    const baseClasses = cn(
      'text-primary-500 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded',
      'transition-colors duration-200',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      sizeClasses[size],
      hasError && 'border-red-500 focus:ring-red-500',
      className
    );

    return (
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            className={baseClasses}
            aria-invalid={hasError}
            aria-describedby={
              hasError && errorText
                ? `${checkboxId}-error`
                : helperText
                  ? `${checkboxId}-helper`
                  : undefined
            }
            {...props}
          />
        </div>
        {(label || helperText || errorText) && (
          <div className="ml-3 text-sm">
            {label && (
              <label
                htmlFor={checkboxId}
                className="font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                {label}
              </label>
            )}
            {hasError && errorText && (
              <p id={`${checkboxId}-error`} className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errorText}
              </p>
            )}
            {!hasError && helperText && (
              <p
                id={`${checkboxId}-helper`}
                className="mt-1 text-sm text-gray-500 dark:text-gray-400"
              >
                {helperText}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
